import React, { useEffect, useMemo, useState } from 'react';
import { updateLscLeague, LscApiError } from '../../api';
import { GAME_KEY_OPTIONS } from '../../constants';
import type { SetupWizardStep } from '../../leagueSetupWizard';
import type { LscLeague } from '../../types';
import ErrorBanner from '../ErrorBanner';
import LeagueSetupStepCard from '../LeagueSetupStepCard';
import LeagueVisibilityPanel from '../LeagueVisibilityPanel';
import LscMutationErrorAlert from '../LscMutationErrorAlert';
import { parseLscMutationError } from '../../entitlementBillingErrors';
import { displaySeasonLabel } from '../../seasonDisplay';

export interface LeagueBasicsStepPanelProps {
  league: LscLeague;
  wizardStep: SetupWizardStep;
  isOwner: boolean;
  isOperator?: boolean;
  leagueArchived?: boolean;
  onLeagueUpdated: (league: LscLeague) => void;
  onSetupRefresh: () => void;
}

function storedStreamingRule(league: LscLeague): '' | 'away' {
  return league.streaming_obligation === 'away' ? 'away' : '';
}

const LeagueBasicsStepPanel: React.FC<LeagueBasicsStepPanelProps> = ({
  league,
  wizardStep,
  isOwner,
  isOperator = false,
  leagueArchived = false,
  onLeagueUpdated,
  onSetupRefresh,
}) => {
  const [name, setName] = useState(league.name);
  const [gameKey, setGameKey] = useState(league.game_key || 'custom');
  const [leagueTag, setLeagueTag] = useState(league.league_tag ?? '');
  const [streamingRule, setStreamingRule] = useState<'' | 'away'>(storedStreamingRule(league));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ReturnType<typeof parseLscMutationError> | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    setName(league.name);
    setGameKey(league.game_key || 'custom');
    setLeagueTag(league.league_tag ?? '');
    setStreamingRule(storedStreamingRule(league));
  }, [league.game_key, league.league_tag, league.name, league.streaming_obligation]);

  const gameLabel = useMemo(
    () => GAME_KEY_OPTIONS.find((option) => option.value === gameKey)?.label ?? league.game_label,
    [gameKey, league.game_label]
  );

  const profileDirty =
    name.trim() !== league.name ||
    gameKey !== (league.game_key || 'custom') ||
    (leagueTag.trim() || null) !== (league.league_tag ?? null) ||
    streamingRule !== storedStreamingRule(league);

  const handleSaveProfile = async () => {
    if (!league.id || !profileDirty || leagueArchived) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await updateLscLeague(league.id, {
        name: name.trim(),
        game_key: gameKey,
        game_label: gameLabel,
        league_tag: leagueTag.trim() || undefined,
        streaming_obligation: streamingRule === 'away' ? 'away' : null,
      });
      onLeagueUpdated(updated);
      setSuccess('League profile saved.');
      onSetupRefresh();
    } catch (err) {
      setError(
        err instanceof LscApiError
          ? parseLscMutationError(err, 'Could not save league profile.')
          : {
              message: 'Could not save league profile.',
              showBillingLink: false,
              showPricingLink: false,
            }
      );
    } finally {
      setSaving(false);
    }
  };

  const handleVisibilityUpdated = (updated: LscLeague) => {
    onLeagueUpdated(updated);
    onSetupRefresh();
  };

  return (
    <div className="space-y-4" data-testid="league-basics-step-panel">
      <LeagueSetupStepCard step={wizardStep} />

      <div className="card space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-white">League profile</h3>
          <p className="text-sm text-secondary-400 mt-1">
            Updates save to your league workspace. URL slug cannot be changed after creation.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="block text-sm text-secondary-300 mb-1" htmlFor="setup-league-name">
              League name
            </label>
            <input
              id="setup-league-name"
              className="input-field"
              value={name}
              onChange={(event) => setName(event.target.value)}
              disabled={leagueArchived}
              data-testid="setup-league-name-input"
            />
          </div>

          <div>
            <label className="block text-sm text-secondary-300 mb-1" htmlFor="setup-league-slug">
              URL slug
            </label>
            <input
              id="setup-league-slug"
              className="input-field opacity-70"
              value={league.slug}
              readOnly
              data-testid="setup-league-slug-readonly"
            />
          </div>

          <div>
            <label className="block text-sm text-secondary-300 mb-1" htmlFor="setup-game-key">
              Competition type
            </label>
            <select
              id="setup-game-key"
              className="input-field"
              value={gameKey}
              onChange={(event) => setGameKey(event.target.value)}
              disabled={leagueArchived}
              data-testid="setup-game-key-select"
            >
              {GAME_KEY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="sm:col-span-2">
            <label className="block text-sm text-secondary-300 mb-1" htmlFor="setup-streaming-rule">
              Streaming rule
            </label>
            <select
              id="setup-streaming-rule"
              className="input-field"
              value={streamingRule}
              onChange={(event) => setStreamingRule(event.target.value === 'away' ? 'away' : '')}
              disabled={leagueArchived || !isOperator}
              data-testid="setup-streaming-rule-select"
            >
              <option value="">No rule</option>
              <option value="away">Away team streams every game</option>
            </select>
            <p className="text-sm text-secondary-500 mt-1">
              When both teams stream a game, the away team's broadcast shows first.
            </p>
          </div>

          <div className="sm:col-span-2">
            <label className="block text-sm text-secondary-300 mb-1" htmlFor="setup-league-tag">
              League tag <span className="text-secondary-500">(optional)</span>
            </label>
            <input
              id="setup-league-tag"
              className="input-field"
              value={leagueTag}
              onChange={(event) => setLeagueTag(event.target.value)}
              placeholder="Short tag used in stream titles"
              disabled={leagueArchived}
              data-testid="setup-league-tag-input"
            />
          </div>
        </div>

        {(league.active_season_label || league.active_stage_label) && (
          <dl className="grid gap-2 sm:grid-cols-2 text-sm">
            <div>
              <dt className="text-secondary-500">Active season</dt>
              <dd className="text-secondary-200">
                {displaySeasonLabel(league.active_season_label) || '—'}
              </dd>
            </div>
            <div>
              <dt className="text-secondary-500">Active week</dt>
              <dd className="text-secondary-200">{league.active_stage_label || '—'}</dd>
            </div>
          </dl>
        )}

        {error ? <LscMutationErrorAlert error={error} onDismiss={() => setError(null)} /> : null}
        {success ? (
          <p className="text-sm text-emerald-300" data-testid="league-basics-save-success">
            {success}
          </p>
        ) : null}

        {!leagueArchived ? (
          <button
            type="button"
            className="btn-primary min-h-11 px-4"
            onClick={() => void handleSaveProfile()}
            disabled={saving || !profileDirty || !name.trim()}
            data-testid="league-basics-save-button"
          >
            {saving ? 'Saving…' : 'Save league profile'}
          </button>
        ) : (
          <ErrorBanner message="League profile cannot be edited while archived." />
        )}
      </div>

      {isOwner ? (
        <LeagueVisibilityPanel
          league={league}
          leagueArchived={leagueArchived}
          onUpdated={handleVisibilityUpdated}
        />
      ) : (
        <p className="text-sm text-secondary-500" data-testid="league-visibility-owner-only">
          League visibility is managed by the league owner.
        </p>
      )}

      <p className="text-xs text-secondary-500" data-testid="league-logo-coming-later">
        Logo and branding controls remain on league settings — coming to this step in a later release.
      </p>
    </div>
  );
};

export default LeagueBasicsStepPanel;
